import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Shield, TrendingUp, PiggyBank, BarChart3, CreditCard, RefreshCw,
  Users, CheckCircle, ChevronRight, Zap, Sparkles, Crown,
} from "lucide-react";
import { PublicNav } from "@/components/layout/PublicNav";
import { PublicFooter } from "@/components/layout/PublicFooter";
import FinanceCalculators from "@/components/homepage/FinanceCalculators";
import ResourceCenter from "@/components/homepage/ResourceCenter";
import { RouteMeta } from "@/components/seo/RouteMeta";

const features = [
  { icon: TrendingUp, title: "Track Every Transaction", description: "Log income and expenses and see your net balance update in real time with smart categorisation." },
  { icon: PiggyBank, title: "Budget Management", description: "Set monthly category budgets and get alerts before you overspend — full data control in one dashboard." },
  { icon: Shield, title: "Transaction Tracking", description: "Track credit cards, bank loans, and payment obligations with payoff projections and custom reporting tools." },
  { icon: BarChart3, title: "Custom Reporting Tools", description: "Build date-range reports, export to CSV or Excel, and visualise your spending data your way." },
  { icon: CreditCard, title: "Personal Productivity Dashboard", description: "One unified dashboard for all your expense data — budgets, savings, tasks, and calendar in one place." },
  { icon: RefreshCw, title: "Automated Data Entry", description: "Set recurring transactions once and let the app auto-log them — zero manual re-entry required." },
];

const steps = [
  { num: "01", title: "Create Your Free Account", desc: "Sign up in seconds — no credit card required. Your data is secured end-to-end." },
  { num: "02", title: "Enter Your Financial Data", desc: "Log transactions, set budgets, create savings targets, and add your payment obligations." },
  { num: "03", title: "Visualise & Export", desc: "Watch your dashboard update in real time. Generate reports, export data, and track trends effortlessly." },
];

const stats = [
  { value: "10+", label: "Data Modules" },
  { value: "100%", label: "Data Privacy" },
  { value: "Free", label: "Core Features" },
  { value: "24/7", label: "Access Anywhere" },
];

const pricingTiers = [
  { name: "Free", price: "$0", period: "forever", icon: Zap, features: ["Unlimited transactions", "3 budget categories", "2 savings goals", "Basic reports", "CSV export"], highlighted: false },
  { name: "Basic Dashboard", price: "$5", period: "per year · from $0.50/mo", icon: Sparkles, features: ["Unlimited budgets & goals", "Recurring transactions", "Excel export & bulk import", "Push notifications", "Weekly email summaries"], highlighted: true, badge: "Most Popular" },
  { name: "Pro Analytics Engine", price: "$8", period: "per year · from $0.80/mo", icon: Crown, features: ["Everything in Basic Dashboard", "Data analytics & portfolio tracking", "AI-powered spending insights", "Multi-currency support", "Priority support"], highlighted: false, badge: "Best Value" },
];

const Index = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <RouteMeta
        title="Financially Fit — Personal Expense & Budgeting Software"
        description="Free personal expense tracker and budgeting software. Log transactions, manage budgets, track savings goals, and export your financial data."
        path="/"
      />
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebApplication",
        name: "Financially Fit", url: "https://financiallyfit.online",
        description: "Personal expense & budgeting software — self-service tool for data organisation, budget management, and transaction tracking.",
        applicationCategory: "FinanceApplication", operatingSystem: "Web, iOS, Android",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        author: { "@type": "Person", name: "Rakibul Hossen" },
        publisher: { "@type": "Organization", name: "Knowledge College BD", url: "https://knowledgecollegebd.com" },
      })}} />

      <PublicNav />

      <main className="flex-1">
        {/* ===== Hero ===== */}
        <header role="banner" className="relative overflow-hidden">
          <div className="absolute inset-0 gradient-primary opacity-5 pointer-events-none" aria-hidden="true" />
          <div className="container mx-auto px-4 py-20 lg:py-28">
            <div className="text-center max-w-3xl mx-auto space-y-6">
              <img
                src="/pwa-192x192.png"
                alt="Financially Fit – personal expense and budgeting software logo"
                width={96} height={96}
                className="w-24 h-24 mx-auto rounded-2xl shadow-lg"
                loading="eager"
                fetchPriority="high"
              />
              <h1 className="text-4xl lg:text-6xl font-bold text-foreground">
                Your Personal{" "}
                <span className="text-primary">Expense & Budgeting Software</span>
              </h1>
              <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto">
                Financially Fit is a self-service SaaS tool for data organisation. Track transactions,
                manage budgets, visualise spending trends, and export custom reports — all for free.
                This is a software tool for data tracking. We do not provide financial, legal, or investment services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button size="lg" onClick={() => navigate("/auth")} className="text-lg px-8"
                  data-gtm-event="hero_getstarted_click" aria-label="Get started with Financially Fit for free">
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/auth")} className="text-lg px-8"
                  data-gtm-event="hero_signin_click" aria-label="Sign in to your Financially Fit account">
                  Sign In
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* ===== Stats Bar ===== */}
        <section aria-label="App statistics" className="bg-primary/5 border-y border-border/50">
          <div className="container mx-auto px-4 py-8">
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center" role="list">
              {stats.map((s) => (
                <li key={s.label}>
                  <p className="text-3xl font-bold text-primary">{s.value}</p>
                  <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ===== Features ===== */}
        <section aria-labelledby="features-heading" className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 id="features-heading" className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
              Everything You Need to Organise Your Financial Data
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Six powerful data modules covering every aspect of your personal finances — all in one self-service platform.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <article key={feature.title}
                className="text-center p-6 rounded-2xl bg-card border border-border/50 card-hover"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-4" aria-hidden="true">
                  <feature.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ===== How It Works ===== */}
        <section aria-labelledby="how-heading" className="bg-secondary/20 border-y border-border/50">
          <div className="container mx-auto px-4 py-20">
            <div className="text-center mb-12">
              <h2 id="how-heading" className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
                How It Works
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Get up and running in minutes — no financial expertise required.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {steps.map((step, i) => (
                <article key={step.num} className="relative text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full gradient-primary text-primary-foreground text-xl font-bold mb-4">
                    {step.num}
                  </div>
                  {i < steps.length - 1 && (
                    <ChevronRight className="hidden md:block absolute top-8 -right-4 h-6 w-6 text-muted-foreground/40" aria-hidden="true" />
                  )}
                  <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Finance Calculators ===== */}
        <FinanceCalculators />

        {/* ===== Resource Center / Learning Paths ===== */}
        <ResourceCenter />

        {/* ===== Pricing Teaser ===== */}
        <section aria-labelledby="pricing-heading" className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 id="pricing-heading" className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
              Simple, Honest Pricing
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Start free — upgrade only when you need more data tools. No hidden fees.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            {pricingTiers.map((tier) => (
              <div key={tier.name}
                className={`relative p-6 rounded-2xl border card-hover text-center ${tier.highlighted ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border/50 bg-card"}`}
              >
                {tier.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold px-3 py-1 rounded-full bg-primary text-primary-foreground">
                    {tier.badge}
                  </span>
                )}
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3 ${tier.highlighted ? "gradient-primary" : "bg-primary/10"}`}>
                  <tier.icon className={`h-6 w-6 ${tier.highlighted ? "text-primary-foreground" : "text-primary"}`} aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-foreground">{tier.name}</h3>
                <p className="text-3xl font-bold text-foreground mt-2">{tier.price}</p>
                <p className="text-sm text-muted-foreground mb-4">/{tier.period}</p>
                <ul className="text-sm text-muted-foreground space-y-1.5 text-left">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button variant="outline" size="lg" onClick={() => navigate("/pricing")}
              data-gtm-event="home_pricing_viewall_click" aria-label="View full pricing details">
              View Full Pricing
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </section>

        {/* ===== Social Proof / Trust ===== */}
        <section aria-labelledby="trust-heading" className="bg-primary/5 border-y border-border/50">
          <div className="container mx-auto px-4 py-16 text-center">
            <h2 id="trust-heading" className="text-3xl font-bold text-foreground mb-4">
              Built for Everyone, Everywhere
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Designed in Bangladesh for the world. Multi-currency data support, global market data integration, and
              budget management tools that work whether you're in Dhaka, Dubai, or New York.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              {["🇧🇩 Bangladesh", "🌍 Global Support", "🔒 Bank-Grade Security", "📱 PWA — Works Offline"].map((item) => (
                <span key={item} className="flex items-center gap-2 px-4 py-2 rounded-full bg-background border border-border/50">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Explore More ===== */}
        <section aria-labelledby="explore-heading" className="bg-secondary/20 border-y border-border/50">
          <div className="container mx-auto px-4 py-12">
            <h2 id="explore-heading" className="text-2xl font-bold text-foreground mb-6 text-center">
              Explore More
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { label: "Read Our Blog", href: "/blog", desc: "Expert articles on budgeting, saving, and investing." },
                { label: "About Us", href: "/about", desc: "Our mission, credentials, and the team behind the app." },
                { label: "View Pricing", href: "/pricing", desc: "Compare Free, Basic, and Pro plans side by side." },
              ].map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block p-4 rounded-xl border border-border/50 bg-card hover:border-primary/40 transition-colors"
                >
                  <p className="font-semibold text-foreground text-sm mb-1">{link.label}</p>
                  <p className="text-xs text-muted-foreground">{link.desc}</p>
                </Link>
              ))}
              <a
                href="https://knowledgecollegebd.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-xl border border-border/50 bg-card hover:border-primary/40 transition-colors"
              >
                <p className="font-semibold text-foreground text-sm mb-1">Knowledge College BD</p>
                <p className="text-xs text-muted-foreground">Our publisher — education, technology, and data literacy.</p>
              </a>
            </div>
          </div>
        </section>

        {/* ===== Disclaimer ===== */}
        <section aria-label="Software disclaimer" className="container mx-auto px-4 py-8 text-center">
          <p className="text-xs text-muted-foreground max-w-2xl mx-auto">
            <strong className="text-foreground">Software Disclaimer:</strong> Financially Fit is a personal expense &amp; budgeting software tool for data organisation and tracking purposes only.
            We do not provide financial, legal, investment, or tax advice. All data entered is managed solely by the user.
          </p>
        </section>

        {/* ===== Final CTA ===== */}
        <section aria-labelledby="cta-heading" className="container mx-auto px-4 py-20 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-2">
              <Users className="h-8 w-8 text-primary" aria-hidden="true" />
            </div>
            <h2 id="cta-heading" className="text-3xl lg:text-4xl font-bold text-foreground">
              Start Organising Your Data Today
            </h2>
            <p className="text-muted-foreground text-lg">
              Join thousands of users who track their budgets smarter with Financially Fit.
              Free to use — no credit card required.
            </p>
            <Button size="lg" onClick={() => navigate("/auth")} className="text-lg px-10"
              data-gtm-event="bottom_cta_click" aria-label="Create your free Financially Fit account">
              Create Free Account
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Button>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default Index;
